import { IAppContext } from 'App/AppContext';
import { Settings } from 'models/setting';
import { UserSettings } from 'models/userSetting';
import CurrentUser from '../currentuser';
import { AuthSchemaCache } from './authSchemaCache';
import { ClassificationCache } from './classificationCache';
import { GroupCache } from './groupCache';
import { NormCache } from './normCache';
import { OutputLocationBindingCache } from './outputLocationBindingCache';
import { OutputLocationCache } from './outputLocationCache';
import { DocumentConnectionCache } from './documentConnectionCache';
import { DocumentProviderCache } from './documentProviderCache';
import { ResourceListCache } from './resourceListCache';
import { RiskStateCache } from './riskStateCache';
import { RoleCache } from './roleCache';
import { TagCache } from './tagCache';
import { TaskStateCache } from './taskStateCache';
import { TaskTypeCache } from './taskTypeCache';
import { TaskViewCache } from './taskViewCache';
import { TemplateCache } from './templateCache';
import { UserCache } from './userCache';

//
// Main global data cache class
//
export type cacheMissFunc = (missedCount: number) => void;

export default class GlobalDataCache {
  appContext?: IAppContext;

  users: UserCache;

  roles: RoleCache;

  groups: GroupCache;

  settings: Settings;

  userSettings: UserSettings;

  tags: TagCache;

  classifications: ClassificationCache;

  lists: ResourceListCache;

  documentProviders: DocumentProviderCache;

  documentConnections: DocumentConnectionCache;

  outputLocationBindings: OutputLocationBindingCache;

  outputLocations: OutputLocationCache;

  riskStates: RiskStateCache;

  taskStates: TaskStateCache;

  taskTypes: TaskTypeCache;

  taskViews: TaskViewCache;

  templates: TemplateCache;

  norms: NormCache;

  authSchemas: AuthSchemaCache;

  constructor() {
    this.users = new UserCache();
    this.roles = new RoleCache();
    this.groups = new GroupCache();
    this.settings = new Settings();
    this.userSettings = new UserSettings();
    this.tags = new TagCache();
    this.classifications = new ClassificationCache();
    this.lists = new ResourceListCache();
    this.documentProviders = new DocumentProviderCache();
    this.documentConnections = new DocumentConnectionCache();
    this.outputLocationBindings = new OutputLocationBindingCache();
    this.outputLocations = new OutputLocationCache();
    this.riskStates = new RiskStateCache();
    this.taskStates = new TaskStateCache();
    this.taskTypes = new TaskTypeCache();
    this.taskViews = new TaskViewCache();
    this.templates = new TemplateCache();
    this.norms = new NormCache();
    this.authSchemas = new AuthSchemaCache();

    this.groups.userCache = this.users;
    this.roles.userCache = this.users;
  }

  setAppContext(appContext: IAppContext) {
    this.appContext = appContext;
    this.users.appContext = this.appContext;
    this.roles.appContext = this.appContext;
    this.groups.appContext = this.appContext;
    this.tags.appContext = this.appContext;
    this.classifications.appContext = this.appContext;
    this.lists.appContext = this.appContext;
    this.documentProviders.appContext = this.appContext;
    this.documentConnections.appContext = this.appContext;
    this.outputLocationBindings.appContext = this.appContext;
    this.outputLocations.appContext = this.appContext;
    this.riskStates.appContext = this.appContext;
    this.taskStates.appContext = this.appContext;
    this.userSettings.appContext = this.appContext;
    this.taskTypes.appContext = this.appContext;
    this.taskViews.appContext = this.appContext;
    this.templates.appContext = this.appContext;
    this.norms.appContext = this.appContext;
    this.authSchemas.appContext = this.appContext;

    this.users.cacheRef = this;
    this.roles.cacheRef = this;
    this.groups.cacheRef = this;
    this.tags.cacheRef = this;
    this.classifications.cacheRef = this;
    this.lists.cacheRef = this;
    this.documentProviders.cacheRef = this;
    this.documentConnections.cacheRef = this;
    this.outputLocationBindings.cacheRef = this;
    this.outputLocations.cacheRef = this;
    this.riskStates.cacheRef = this;
    this.taskStates.cacheRef = this;
    this.taskTypes.cacheRef = this;
    this.taskViews.cacheRef = this;
    this.templates.cacheRef = this;
    this.norms.cacheRef = this;
    this.authSchemas.cacheRef = this;
  }

  setCurrentUserLanguage(user: CurrentUser) {
    this.classifications.setCurrentUserLanguage(user);
    this.riskStates.setCurrentUserLanguage(user);
    this.taskStates.setCurrentUserLanguage(user);
  }

  async refresh() {
    await this.users.getItems(true);
    await this.roles.getItems(true);
    await this.groups.getItems(true);
    await this.tags.getItems(true);
    // the lists cache refreshes documentProviders, documentConnections, outputLocationBindings and outputLocations
    // with it: every category read needs its provider's capabilities, so they travel together
    await this.lists.getItems(true);
    await this.taskTypes.getItems(true);
    await this.taskViews.getItems(true);
    await this.templates.getItems(true);
    await this.norms.getItems(true);
    await this.authSchemas.getItems(true);
    this.users.cachePhotos(); //asynchronously cache user photos
  }

  clone(): GlobalDataCache {
    const newGlobalDataCache = new GlobalDataCache();

    newGlobalDataCache.groups = this.groups.clone();
    newGlobalDataCache.users = this.users.clone();
    newGlobalDataCache.roles = this.roles.clone();
    newGlobalDataCache.settings = this.settings.clone();
    newGlobalDataCache.userSettings = this.userSettings.clone();
    newGlobalDataCache.tags = this.tags.clone();
    newGlobalDataCache.classifications = this.classifications.clone();
    newGlobalDataCache.lists = this.lists.clone();
    newGlobalDataCache.documentProviders = this.documentProviders.clone();
    newGlobalDataCache.documentConnections = this.documentConnections.clone();
    newGlobalDataCache.outputLocationBindings = this.outputLocationBindings.clone();
    newGlobalDataCache.outputLocations = this.outputLocations.clone();
    newGlobalDataCache.riskStates = this.riskStates.clone();
    newGlobalDataCache.taskStates = this.taskStates.clone();
    newGlobalDataCache.taskTypes = this.taskTypes.clone();
    newGlobalDataCache.taskViews = this.taskViews.clone();
    newGlobalDataCache.templates = this.templates.clone();
    newGlobalDataCache.norms = this.norms.clone();
    newGlobalDataCache.authSchemas = this.authSchemas.clone();

    newGlobalDataCache.appContext = this.appContext;
    newGlobalDataCache.users.appContext = this.appContext;
    newGlobalDataCache.roles.appContext = this.appContext;
    newGlobalDataCache.groups.appContext = this.appContext;
    newGlobalDataCache.tags.appContext = this.appContext;
    newGlobalDataCache.classifications.appContext = this.appContext;
    newGlobalDataCache.lists.appContext = this.appContext;
    newGlobalDataCache.documentProviders.appContext = this.appContext;
    newGlobalDataCache.documentConnections.appContext = this.appContext;
    newGlobalDataCache.outputLocationBindings.appContext = this.appContext;
    newGlobalDataCache.outputLocations.appContext = this.appContext;
    newGlobalDataCache.riskStates.appContext = this.appContext;
    newGlobalDataCache.taskStates.appContext = this.appContext;
    newGlobalDataCache.userSettings.appContext = this.appContext;
    newGlobalDataCache.taskTypes.appContext = this.appContext;
    newGlobalDataCache.taskViews.appContext = this.appContext;
    newGlobalDataCache.templates.appContext = this.appContext;
    newGlobalDataCache.norms.appContext = this.appContext;
    newGlobalDataCache.authSchemas.appContext = this.appContext;

    newGlobalDataCache.users.cacheRef = newGlobalDataCache;
    newGlobalDataCache.roles.cacheRef = newGlobalDataCache;
    newGlobalDataCache.groups.cacheRef = newGlobalDataCache;
    newGlobalDataCache.tags.cacheRef = newGlobalDataCache;
    newGlobalDataCache.classifications.cacheRef = newGlobalDataCache;
    newGlobalDataCache.lists.cacheRef = newGlobalDataCache;
    newGlobalDataCache.documentProviders.cacheRef = newGlobalDataCache;
    newGlobalDataCache.documentConnections.cacheRef = newGlobalDataCache;
    newGlobalDataCache.outputLocationBindings.cacheRef = newGlobalDataCache;
    newGlobalDataCache.outputLocations.cacheRef = newGlobalDataCache;
    newGlobalDataCache.riskStates.cacheRef = newGlobalDataCache;
    newGlobalDataCache.taskStates.cacheRef = newGlobalDataCache;
    newGlobalDataCache.taskTypes.cacheRef = newGlobalDataCache;
    newGlobalDataCache.taskViews.cacheRef = newGlobalDataCache;
    newGlobalDataCache.templates.cacheRef = newGlobalDataCache;
    newGlobalDataCache.norms.cacheRef = newGlobalDataCache;
    newGlobalDataCache.authSchemas.cacheRef = newGlobalDataCache;

    return newGlobalDataCache;
  }

  clear() {
    this.users = new UserCache();
    this.roles = new RoleCache();
    this.groups = new GroupCache();
    this.settings = new Settings();
    this.userSettings = new UserSettings();
    this.tags = new TagCache();
    this.classifications = new ClassificationCache();
    this.lists = new ResourceListCache();
    this.documentProviders = new DocumentProviderCache();
    this.documentConnections = new DocumentConnectionCache();
    this.outputLocationBindings = new OutputLocationBindingCache();
    this.outputLocations = new OutputLocationCache();
    this.riskStates = new RiskStateCache();
    this.taskStates = new TaskStateCache();
    this.taskTypes = new TaskTypeCache();
    this.taskViews = new TaskViewCache();
    this.templates = new TemplateCache();
    this.norms = new NormCache();
    this.authSchemas = new AuthSchemaCache();

    this.groups.userCache = this.users;
    this.roles.userCache = this.users;
  }
}
